import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ChipModule } from 'primeng/chip';
import { NavController } from '@ionic/angular/standalone';
import { TicketService } from '../../../../services/ticket.service';
import { ToastService } from '../../../../services/toast.service';
import { CustomerTicket, MyTicketsResponse } from '../../../../interfaces/ticket';

@Component({
  selector: 'app-tickets-list',
  templateUrl: './tickets-list.component.html',
  styleUrls: ['./tickets-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    SkeletonModule,
    ChipModule
  ]
})
export class TicketsListComponent implements OnInit {
  tickets: CustomerTicket[] = [];
  isLoading = true;

  constructor(
    private _nav: NavController,
    private _ticketService: TicketService,
    private _toast: ToastService
  ) {}

  ngOnInit() {
    this.loadTickets();
  }

  async loadTickets() {
    this.isLoading = true;
    try {
      this._ticketService.getMyTickets().subscribe({
        next: (response: MyTicketsResponse) => {
          // Show only active tickets, max 5
          this.tickets = response.grouped.active.slice(0, 5);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading tickets:', error);
          this._toast.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Tickets konnten nicht geladen werden'
          });
          this.isLoading = false;
        }
      });
    } catch (error) {
      console.error('Error loading tickets:', error);
      this.isLoading = false;
    }
  }

  viewTicket(ticket: CustomerTicket) {
    this._nav.navigateForward(['/dashboard/my-tickets', ticket.id]);
  }

  getTicketTypeLabel(type?: string): string {
    if (!type) return 'Ticket';
    return this._ticketService.getTicketTypeLabel(type);
  }

  getTicketTypeIcon(type?: string): string {
    if (!type) return 'pi pi-ticket';
    return this._ticketService.getTicketTypeIcon(type);
  }

  getStatusLabel(status: string): string {
    return this._ticketService.getStatusLabel(status);
  }

  getStatusChipClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'expired':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'exhausted':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'cancelled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-surface-100 text-surface-600';
    }
  }

  getIconBgClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/30';
      case 'expired':
        return 'bg-yellow-100 dark:bg-yellow-900/30';
      case 'exhausted':
        return 'bg-blue-100 dark:bg-blue-900/30';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/30';
      default:
        return 'bg-surface-100 dark:bg-surface-800';
    }
  }

  getIconColorClass(status: string): string {
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400';
      case 'expired':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'exhausted':
        return 'text-blue-600 dark:text-blue-400';
      case 'cancelled':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-surface-600 dark:text-surface-400';
    }
  }

  getUsageProgress(ticket: CustomerTicket): number {
    if (!ticket.usesInfo) return 0;
    return (ticket.usesInfo.used / ticket.usesInfo.total) * 100;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Calculate remaining days until expiration
   */
  getDaysRemaining(validUntil?: string): number | null {
    if (!validUntil) return null;
    const now = new Date();
    const expiry = new Date(validUntil);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Check if ticket expires soon (within 10 days)
   */
  isExpiringSoon(validUntil?: string): boolean {
    const days = this.getDaysRemaining(validUntil);
    return days !== null && days <= 10 && days > 0;
  }

  /**
   * Check if ticket is expired
   */
  isExpired(validUntil?: string): boolean {
    const days = this.getDaysRemaining(validUntil);
    return days !== null && days <= 0;
  }

  /**
   * Get formatted remaining days text
   */
  getRemainingDaysText(validUntil?: string): string {
    const days = this.getDaysRemaining(validUntil);
    if (days === null) return '';
    if (days <= 0) return 'Abgelaufen';
    if (days === 1) return 'Noch 1 Tag';
    return `Noch ${days} Tage`;
  }
}
